import React, { useEffect, useMemo, useState } from "react";
import { brandPalette } from "../../../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Select from "react-select";
import MainModel from "../../../../components/Models/main-model";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
} from "../../../../components/Models/admin-modal-theme";
import MainInput from "../../../../components/Form/input";
import { closeOfficeHourModal } from "../../../../modules/admin-modules/models/Actions";
import {
  createOfficeHourRequest,
  updateOfficeHourRequest,
} from "../../../../modules/admin-modules/office_hours/Actions";
import { getAssistantsRequest } from "../../../../modules/admin-modules/teachersAssistant/Actions";

const modalSelectStyles = {
  ...adminSelectStyles(),
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
};

/** API datetime → value for input[type=datetime-local] */
function apiDateToDatetimeLocal(val) {
  if (val == null || val === "") return "";
  const s = String(val).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(:\d{2})?/);
  if (m) return `${m[1]}T${m[2]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return "";
}

/** datetime-local → Laravel-friendly "Y-m-d H:i:s" */
function datetimeLocalToApi(val) {
  if (val == null || String(val).trim() === "") return null;
  const raw = String(val).trim();
  const withSeconds = raw.length === 16 ? `${raw}:00` : raw;
  return withSeconds.replace("T", " ");
}

function resolveAssistantId(data) {
  if (data == null) return "";
  return data.assistant_id ?? data.assistant?.id ?? "";
}

const AddOfficeHour = ({ isAdd, data, setSelectedData }) => {
  const openModal = useSelector((state) => state.model.isOpen43);
  const isLoading = useSelector((state) => state.officeHours.isLoading);
  const assistants = useSelector((state) => state.teacherassistants.assistants);
  const dispatch = useDispatch();
  const [assistantId, setAssistantId] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      from: "",
      to: "",
      zoom_link: "",
    },
  });

  const assistantsOptions = useMemo(
    () =>
      assistants?.data?.map((a) => ({
        value: a.id,
        label: a.name,
      })) ?? [],
    [assistants]
  );

  const selectedAssistant = useMemo(
    () => assistantsOptions.find((o) => String(o.value) === String(assistantId)) || null,
    [assistantsOptions, assistantId]
  );

  useEffect(() => {
    dispatch(getAssistantsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (data && data.id != null && !isAdd) {
      reset({
        title: data.title ?? "",
        from: apiDateToDatetimeLocal(data.from),
        to: apiDateToDatetimeLocal(data.to),
        zoom_link: data.zoom_link ?? "",
      });
      setAssistantId(resolveAssistantId(data));
    } else {
      reset({
        title: "",
        from: "",
        to: "",
        zoom_link: "",
      });
      setAssistantId("");
    }
  }, [data, isAdd, reset]);

  const header = useMemo(
    () => (
      <Box>
        <Heading
          fontSize="32px"
          fontWeight="800"
          color={brandPalette.primary}
          fontFamily="'Amiri','Tajawal',sans-serif"
        >
          {isAdd ? "Add office hour" : "Edit office hour"}
        </Heading>
        <Text
          fontSize="xs"
          mt={1}
          color="var(--text-muted)"
          textTransform="uppercase"
          letterSpacing="0.14em"
          fontWeight="700"
        >
          Title, schedule, assistant, and Meeting link
        </Text>
      </Box>
    ),
    [isAdd]
  );

  const onSubmit = (formData) => {
    const body = {
      title: formData.title?.trim() || "",
      from: datetimeLocalToApi(formData.from),
      to: datetimeLocalToApi(formData.to),
      zoom_link: formData.zoom_link?.trim() || null,
      assistant_id: assistantId || null,
    };

    const onDone = () => {
      dispatch(closeOfficeHourModal());
      setSelectedData({});
      setAssistantId("");
      reset();
    };

    if (isAdd) {
      dispatch(createOfficeHourRequest(body, onDone));
    } else {
      dispatch(updateOfficeHourRequest({ id: data.id, body }, onDone));
    }
  };

  const closeFunc = () => {
    dispatch(closeOfficeHourModal());
    setSelectedData({});
    setAssistantId("");
    reset();
  };

  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openModal}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="2xl"
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={adminModalBodyProps}
      hideDefaultFooter
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={6} dir="ltr" w="100%">
          <MainInput
            label="Title"
            error={errors?.title?.message}
            placeholder="e.g. Chemistry drop-in"
            register={register}
            name="title"
            required
          />

          <MainInput
            type="datetime-local"
            label="From"
            error={errors?.from?.message}
            placeholder=""
            register={register}
            name="from"
            required
          />

          <MainInput
            type="datetime-local"
            label="To"
            error={errors?.to?.message}
            placeholder=""
            register={register}
            name="to"
            required
          />

          <Box w="100%">
            <FormLabel fontWeight="800" color="var(--text-primary)">
              Assistant
            </FormLabel>
            <Select
              styles={modalSelectStyles}
              options={assistantsOptions}
              value={selectedAssistant}
              isClearable
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              onChange={(option) => setAssistantId(option?.value ?? "")}
              placeholder="Select assistant"
            />
          </Box>

          <MainInput
            label="Meeting link"
            error={errors?.zoom_link?.message}
            placeholder="https://zoom.us/j/…"
            register={register}
            name="zoom_link"
            required={false}
          />

          <Flex justify="flex-end" gap={6} pt={4}>
            <Button
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isLoading}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save
            </Button>
          </Flex>
        </Stack>
      </form>
    </MainModel>
  );
};

export default AddOfficeHour;
